import { BlobProvider, PDFViewer } from '@react-pdf/renderer';
import { NotificationManager } from 'react-notifications';

import { finalizeContract, getDataToFinalizeContract } from '../../../../../../services/contracts.service';
import SaralLoaderImage from '../../../../../common-components/SaralLoaderImage';
import ContractReactPdfPreview from '../PdfPreviewModal/ContractReactPdfPreview/ContractReactPdfPreview';

import './_influencerSigning.scss';

import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useParams } from 'react-router-dom';

import SignContractModal from '../SignContractModal/SignContractModal';
import PDFAnnotator from './PDFAnnotator';
import SigningHeader from './SigningHeader';

export default function InfluencerSigning() {
  const { userId, contractId } = useParams();

  const [influencerSignedAt, setInfluencerSignedAt] = useState();
  const [contractFinalizeDetails, setContractFinalizeDetails] = useState({});

  const [showSignatureModal, setShowSignatureModal] = useState(false);

  const [influencerSignatureMeta, setInfluencerSignatureMeta] = useState(null);

  const [isLoading, setIsLoading] = useState(true);

  const [signatureFieldPosition, setSignatureFieldPosition] = useState(null);

  const [generatedPdfBlob, setGeneratedPdfBlob] = useState(null);
  const pendingFinalizationRef = useRef(null);

  const fetchContractDetails = async () => {
    setIsLoading(true);
    try {
      const finalizeDetails = await getDataToFinalizeContract({ contractId, userId });
      setContractFinalizeDetails(finalizeDetails?.data);
    } catch (error) {
      console.log('failed to fetch details', error);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchContractDetails();
  }, []);

  useEffect(() => {
    if (contractFinalizeDetails?.type === 'custom') {
      const { contractFields, selectedTemplate } = contractFinalizeDetails.formData.meta;

      const influencerSignatureField = contractFields.find((field) => field.type === 'influencer-signature');

      if (influencerSignatureField && selectedTemplate?.fields) {
        const fieldPosition = selectedTemplate.fields.find((field) => field.field === influencerSignatureField._id);

        if (fieldPosition) {
          setSignatureFieldPosition({
            page: fieldPosition.page,
            x: fieldPosition.x,
            y: fieldPosition.y,
            width: fieldPosition.width,
            height: fieldPosition.height,
          });
        }
      }
    }
  }, [contractFinalizeDetails]);

  // Watch for PDF regeneration and finalize when ready
  useEffect(() => {
    if (pendingFinalizationRef.current && generatedPdfBlob) {
      const finalize = pendingFinalizationRef.current;
      pendingFinalizationRef.current = null;
      finalize(generatedPdfBlob);
    }
  }, [generatedPdfBlob]);

  let userSignatureMeta;
  if (contractFinalizeDetails?.textSignatureId?.text) {
    userSignatureMeta = {
      type: 'typed',
      text: contractFinalizeDetails?.textSignatureId?.text,
      fontFamily: contractFinalizeDetails?.textSignatureId?.fontStyling?.fontFamily,
    };
  } else if (contractFinalizeDetails?.imageSignatureId) {
    userSignatureMeta = {
      type: 'image',
      src: contractFinalizeDetails?.imageSignatureId?.imageSignatureUrl,
    };
  }

  const reactPdfDocument = (
    <ContractReactPdfPreview
      formData={contractFinalizeDetails?.formData?.formData}
      meta={contractFinalizeDetails?.formData?.meta}
      userSignatureMeta={userSignatureMeta}
      influencerSignatureMeta={influencerSignatureMeta}
      influencerSignedAt={influencerSignedAt}
    />
  );

  const handleContractFinalization = async (signatureData) => {
    setInfluencerSignatureMeta(signatureData);
    setInfluencerSignedAt(new Date());
    setShowSignatureModal(false);

    try {
      if (contractFinalizeDetails?.type === 'custom') {
        // TODO - i don't understand this
        // Store finalization function to be called when PDF is regenerated
        pendingFinalizationRef.current = async (pdfBlob) => {
          try {
            await fetch(contractFinalizeDetails.finalizeUploadUrl, {
              method: 'PUT',
              body: pdfBlob,
            });

            await finalizeContract({ contractId, userId });
            NotificationManager.success('Contract finalized successfully', '', 1200);
          } catch (error) {
            console.error('Error finalizing contract:', error);
            NotificationManager.error(`Failed to finalize contract: ${error.message}`, '', 1200);
          }
        };
        // useEffect will call this when generatedPdfBlob updates
        return;
      } else {
        // Get the PDF blob using BlobProvider component
        const blob = await new Promise((resolve, reject) => {
          const element = (
            <BlobProvider
              document={
                <ContractReactPdfPreview
                  formData={contractFinalizeDetails?.formData?.formData}
                  meta={contractFinalizeDetails?.formData?.meta}
                  userSignatureMeta={userSignatureMeta}
                  influencerSignatureMeta={signatureData}
                  influencerSignedAt={new Date()}
                />
              }>
              {({ blob, loading, error }) => {
                if (loading) return null;
                if (error) reject(error);
                if (blob) resolve(blob);
                return null;
              }}
            </BlobProvider>
          );

          ReactDOM.render(element, document.createElement('div'));
        });

        const response = await fetch(contractFinalizeDetails.finalizeUploadUrl, {
          method: 'PUT',
          body: blob,
        });

        if (!response.ok) {
          throw new Error(`Upload failed with status ${response.status}`);
        }
      }

      await finalizeContract({ contractId, userId });
      NotificationManager.success('Contract finalized successfully', '', 1200);
    } catch (error) {
      console.error('Error finalizing contract:', error);
      NotificationManager.error(`Failed to finalize contract: ${error.message}`, '', 1200);
    }
  };

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 100 }}>
        <SaralLoaderImage />
      </div>
    );
  }

  if (contractFinalizeDetails?.type === 'custom') {
    return (
      <div className="influencer-signing-container">
        <SigningHeader
          influencerSignatureMeta={influencerSignatureMeta}
          setShowSignatureModal={setShowSignatureModal}
        />
        <div className="pdf-container">
          <PDFAnnotator
            pdfUrl={contractFinalizeDetails.draftUrl}
            influencerSignatureMeta={influencerSignatureMeta}
            onPdfGenerated={(blob) => setGeneratedPdfBlob(blob)}
          />
        </div>
        {showSignatureModal && (
          <SignContractModal
            isInfluencerSigning
            isOpen={showSignatureModal}
            onClose={() => setShowSignatureModal(false)}
            onUploadFinish={(data) => {
              const signatureWithPosition = {
                ...data,
                position: signatureFieldPosition,
              };
              handleContractFinalization(signatureWithPosition);
            }}
          />
        )}
      </div>
    );
  }

  return (
    <div className="influencer-signing-container">
      <SigningHeader influencerSignatureMeta={influencerSignatureMeta} setShowSignatureModal={setShowSignatureModal} />
      <div className="pdf-container">
        <PDFViewer width={'100%'} height={'100%'} showToolbar>
          {reactPdfDocument}
        </PDFViewer>
      </div>
      {showSignatureModal && (
        <SignContractModal
          isInfluencerSigning
          isOpen={showSignatureModal}
          onClose={() => setShowSignatureModal(false)}
          onUploadFinish={handleContractFinalization}
        />
      )}
    </div>
  );
}
